import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Message, Modal } from '@bedrock/components';
import { ProTablePlus } from '@bedrock/eed-react';
import { useMemoizedFn } from 'ahooks';
import withFunctionCall from '@mpc/common/src/utils/withFunctionCall';
import { v2SegmentSupplierBiddingsSearch } from '@mpc/common/src/services/api';
import type { BiddingSimpleVo } from '@mpc/common/src/services/api';

// ============ 类型定义 ============

export interface BiddingDemandInfo {
  demand_id: number;
  demand_no: string;
  product_name: string;
  demand_title: string;
  project_no: string;
  bidding_type: string;
}

interface BiddingDemandSelectModalProps {
  supplierId: number;
  /** 已选中的比选单 ID（用于弹框内回显预选） */
  selectedDemandId?: number | null;
  onSelect: (demand: BiddingDemandInfo) => void;
}

/** 打开比选单详情页（新标签页） */
function openBiddingDetail(biddingId: number) {
  window.open(
    `${location.origin}/#/bidding?projectId=${biddingId}`,
    '_blank',
  );
}

// ============ 内部组件 ============

const BiddingDemandSelectModalInner = ({
  supplierId,
  selectedDemandId,
  onSelect,
  ...rest
}: BiddingDemandSelectModalProps & Record<string, any>) => {
  const tableRef = useRef<any>(null);
  const [selectedRowKey, setSelectedRowKey] = useState<number | null>(
    selectedDemandId ?? null,
  );

  // 外部 selectedDemandId 变化时同步选中（弹框复用时保持回显）
  useEffect(() => {
    setSelectedRowKey(selectedDemandId ?? null);
  }, [selectedDemandId]);

  // ============ 表格列配置 ============
  // 注：后端 search API 仅支持 keyword 搜索，因此列级筛选项已移除，
  // 统一使用顶部分页工具栏的 keyword 搜索框

  const columns = useMemo(
    () => [
      {
        title: '比选单号',
        dataIndex: 'demand_id',
        width: 150,
        render: (text: any, record: BiddingDemandInfo) => (
          <Button
            type="link"
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation(); // 防止触发行选择
              openBiddingDetail(record.demand_id);
            }}>
            {record.demand_id}
          </Button>
        ),
      },
      {
        title: '所属产品',
        dataIndex: 'product_name',
        width: 100,
      },
      {
        title: '需求标题',
        dataIndex: 'demand_title',
        width: 200,
      },
      {
        title: '项目编号',
        dataIndex: 'project_no',
        width: 150,
      },
      {
        title: '比选类型',
        dataIndex: 'bidding_type',
        width: 100,
      },
    ],
    [],
  );

  // ============ 数据查询服务 ============

  const queryService = useMemoizedFn(async (query: any) => {
    try {
      const params = query?.params || {};
      const keyword: string = params.keyword || '';

      const res = await v2SegmentSupplierBiddingsSearch({ keyword });
      const list = (res?.data || []).map((item: BiddingSimpleVo) => ({
        demand_id: item.id,
        demand_no: item.number || '',
        product_name: item.product_name || '',
        demand_title: item.name || '',
        project_no: item.number || '',
        bidding_type: item.bidding_type_name || '',
      }));

      return {
        data: {
          list,
          total: list.length,
        },
      };
    } catch (err: any) {
      Message.error(err?.message || '加载比选单列表失败');
      return { data: { list: [], total: 0 } };
    }
  });

  // ============ 选择确认 ============

  const handleConfirm = useMemoizedFn(() => {
    if (selectedRowKey === null) {
      Message.warning('请选择一个比选单');
      return;
    }
    // 通过 tableRef 获取选中行
    const selectedRows = tableRef.current?.getSelectedRows?.() || [];
    const selected = selectedRows.find((r: BiddingDemandInfo) => r.demand_id === selectedRowKey);
    if (selected) {
      onSelect(selected);
    }
    rest?.onCancel?.();
  });

  // ============ 行选择变更 ============

  const handleRowSelectChange = useMemoizedFn((selectedRowKeys: React.Key[]) => {
    setSelectedRowKey((selectedRowKeys[0] as number) ?? null);
  });

  // ============ 渲染 ============

  return (
    <Modal
      {...rest}
      title="选择关联比选单"
      width={800}
      onCancel={() => rest?.onCancel?.()}
      onOk={handleConfirm}
      maskClosable={false}
      destroyOnClose>
      <div className="pt-1">
        <ProTablePlus
          ref={tableRef}
          rowKey="demand_id"
          columns={columns}
          request={{ queryService }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
            onChange: handleRowSelectChange,
          }}
          onRow={(record: BiddingDemandInfo) => ({
            onClick: () => setSelectedRowKey(record.demand_id),
          })}
          toolbar={{
            search: {
              placeholder: '请输入比选单号或需求标题',
              id: 'keyword',
            },
          }}
          pagination={false}
          scroll={{ y: 360 }}
          size="small"
        />
      </div>
    </Modal>
  );
};

BiddingDemandSelectModalInner.displayName = 'BiddingDemandSelectModal';

// ============ 导出：withFunctionCall 包装，暴露 .show() 命令式调用 ============

export const BiddingDemandSelectModal = withFunctionCall(BiddingDemandSelectModalInner);
